import type { LlmModel, LlmModelSelectDto } from '@@types/api.types';
import { LlmChatModal } from '@components/shared/llm-chat/llm-chat-modal.component';
import { type PromptGuidelines, PromptPopover } from '@components/shared/prompt-popover.component';
import { Button, type ButtonProps } from '@components/ui/button';
import { errorToast } from '@hooks/toast.hook';
import { llmModelStore } from '@stores/llm-model.store';
import { isStructuredOutputModelSelectable } from '@utils/llm-model.util';
import React from 'react';
import { useStore } from 'zustand';

type GenerateJSONRequestParams = {
  model?: LlmModel;
  prompt?: string;
  signal?: AbortSignal;
};

export type GenerateJSONButtonProps<TData extends object, TResult> = {
  abortable?: boolean;
  buttonProps?: ButtonProps;
  /**
   * Chat endpoint for the fullscreen LlmChatModal workspace. Providing it together
   * with `onAcceptData` makes Maximize open the structured-object chat.
   */
  chatApiEndpoint?: string;
  /**
   * Request context sent with every chat exchange (e.g. objectKind, technologyId).
   */
  chatData?: Record<string, unknown>;
  className?: string;
  description?: React.ReactNode;
  generatingChildren?: React.ReactNode;
  information?: PromptGuidelines;
  isGenerating?: boolean;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  mutateAsync: (params: TData & GenerateJSONRequestParams) => Promise<TResult>;
  /**
   * Accept a structured-object Suggestion from the fullscreen chat workspace.
   */
  onAcceptData?: (data: Record<string, unknown>) => void;
  onGeneratingEnd?: () => void;
  onGeneratingStart?: () => void;
  onSuccess: (result: TResult) => void;
  /**
   * Optional surface-specific renderer for the chat's structured-object drafts.
   */
  renderDataArtifact?: (data: Record<string, unknown>) => React.ReactNode;
  params: TData;
  placeholder?: string;
  title?: string;
};

export const GenerateJSONButton = <TData extends object, TResult>(props: GenerateJSONButtonProps<TData, TResult>) => {
  const {
    abortable = false,
    buttonProps,
    chatApiEndpoint,
    chatData,
    className,
    description,
    generatingChildren,
    information,
    isGenerating = false,
    modelFilter,
    mutateAsync,
    onAcceptData,
    onGeneratingEnd,
    onGeneratingStart,
    onSuccess,
    params,
    placeholder,
    renderDataArtifact,
    title,
  } = props;

  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const selectedModel = useStore(llmModelStore, (state) => state.selectedModel);
  const [prompt, setPrompt] = React.useState('');
  const effectiveModelFilter = modelFilter ?? isStructuredOutputModelSelectable;
  const activeGenerationRef = React.useRef(0);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const stoppedGenerationRef = React.useRef<number | null>(null);
  const effectiveIsLoading = isLoading || isGenerating;

  const stopGeneration = React.useCallback(() => {
    if (!effectiveIsLoading) {
      return;
    }

    stoppedGenerationRef.current = activeGenerationRef.current;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    onGeneratingEnd?.();
  }, [effectiveIsLoading, onGeneratingEnd]);

  const submitGeneration = async () => {
    if (effectiveIsLoading) {
      return;
    }

    const generationId = activeGenerationRef.current + 1;
    const abortController = abortable ? new AbortController() : null;

    activeGenerationRef.current = generationId;
    abortControllerRef.current = abortController;
    stoppedGenerationRef.current = null;
    setIsLoading(true);
    onGeneratingStart?.();

    try {
      const result = await mutateAsync({
        ...params,
        ...(selectedModel && { model: selectedModel }),
        ...(prompt && { prompt }),
        ...(abortController && { signal: abortController.signal }),
      });

      if (activeGenerationRef.current === generationId && stoppedGenerationRef.current !== generationId) {
        onSuccess(result);
        setPrompt('');
      }
    } catch (error) {
      if (stoppedGenerationRef.current !== generationId) {
        console.error('Error generating content:', error);
        errorToast({ description: 'Failed to generate content' });
      }
    } finally {
      if (activeGenerationRef.current === generationId && stoppedGenerationRef.current !== generationId) {
        setIsLoading(false);
        onGeneratingEnd?.();
      }

      if (stoppedGenerationRef.current === generationId) {
        stoppedGenerationRef.current = null;
      }

      if (activeGenerationRef.current === generationId) {
        abortControllerRef.current = null;
      }
    }
  };

  const handlePromptSubmit = () => {
    return submitGeneration();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void handlePromptSubmit();
    }
  };

  // Show generating state on the button
  const effectiveButtonProps = isGenerating
    ? {
        ...buttonProps,
        children: generatingChildren ?? 'Generating...',
        disabled: true,
        className: 'animate-pulse duration-1000',
      }
    : buttonProps;

  const handleOpenChat = () => {
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
    // The typed prompt was handed off to the chat; reopening the popover starts fresh.
    setPrompt('');
  };

  return (
    <>
      <PromptPopover
        description={description}
        information={information}
        isLoading={effectiveIsLoading}
        modelFilter={effectiveModelFilter}
        onExpand={chatApiEndpoint && onAcceptData ? handleOpenChat : undefined}
        onSubmit={handlePromptSubmit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        prompt={prompt}
        setPrompt={setPrompt}
        stop={stopGeneration}
        title={title || 'Generate with AI'}
        trigger={
          <Button
            className={className}
            disabled={effectiveIsLoading}
            type="button"
            {...effectiveButtonProps}
          />
        }
      />

      {chatApiEndpoint && onAcceptData && (
        <LlmChatModal
          apiEndpoint={chatApiEndpoint}
          data={chatData ?? {}}
          description={description}
          information={information}
          initialInput={prompt}
          isOpen={isChatOpen}
          modelFilter={effectiveModelFilter}
          onAcceptData={onAcceptData}
          onClose={handleChatClose}
          renderDataArtifact={renderDataArtifact}
          title={title || 'Generate with AI'}
        />
      )}
    </>
  );
};
